import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
  getApiV1WeddingsByWeddingIdActivitiesRandomNumberStateOptions,
  getApiV1WeddingsByWeddingIdAuthMeOptions,
} from '@wedding-tech/api-client/tanstack'
import { Button } from '@wedding-tech/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@wedding-tech/ui/card'
import canvasConfetti from 'canvas-confetti'
import { Sparkles } from 'lucide-react'
import { AnimatePresence, domAnimation, LazyMotion, m } from 'motion/react'
import { useCallback, useEffect, useRef, useState } from 'react'

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { drawRandomNumber, type RandomNumberState } from '@/lib/activities'
import { useLIFF } from '@/lib/liff/context'

/**
 * This is a control screen, not a passive display — the player already gets
 * their own draw result immediately from drawRandomNumber(). Polling here
 * only needs to catch the admin starting/ending a round, so it can be a lot
 * slower than a projector's refresh; there is no WebSocket layer on this
 * route (see apps/api's display/ws — that's display-token gated, guests
 * don't have one).
 */
const GAME_STATE_POLL_MS = 4000
import MdiHistory from '~icons/mdi/history'
import MdiNumeric from '~icons/mdi/numeric'
import MdiPlay from '~icons/mdi/play'
import MdiRefresh from '~icons/mdi/refresh'

export const Route = createFileRoute(
  '/e/$eventId/_dashboard/activities/random-number',
)({
  component: RandomNumberGame,
})

const getRandomNumber = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min + 1)) + min

function RandomNumberGame() {
  const { eventId } = Route.useParams()
  const { profile: liffProfile } = useLIFF()
  const { data: authMe } = useQuery(
    getApiV1WeddingsByWeddingIdAuthMeOptions({ path: { weddingId: eventId } }),
  )
  const { data: gameStateData } = useQuery({
    ...getApiV1WeddingsByWeddingIdActivitiesRandomNumberStateOptions({
      path: { weddingId: eventId },
    }),
    refetchInterval: GAME_STATE_POLL_MS,
  })
  // Guest-safe subset only — targetNumber/forceNextWin are stripped
  // server-side (see apps/api's toGuestSafeRandomNumberData), so a guest
  // never has the answer to the game they're playing sitting in state.
  const gameState = (gameStateData?.data ?? null) as RandomNumberState | null
  const [currentNumber, setCurrentNumber] = useState<number | null>(null)
  const [isSpinning, setIsSpinning] = useState(false)
  const isSpinningRef = useRef(false)
  const isRevealDelayingRef = useRef(false)
  const [history, setHistory] = useState<number[]>([])
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [hasWon, setHasWon] = useState(false)
  // The number that actually won — from the draw response, never from
  // gameState (which can't carry it; see above). Used both for the winner
  // banner and to mark the matching entry in the guest's own history.
  const [winningNumber, setWinningNumber] = useState<number | null>(null)
  const [particles, setParticles] = useState<
    Array<{
      id: number
      left: number
      top: number
      size: number
      delay: number
      duration: number
    }>
  >([])

  const profile = authMe?.profile
  const userId =
    liffProfile?.userId || profile?.fullName || profile?.displayName

  // Generate stable rose particles once on mount
  useEffect(() => {
    const items = Array.from({ length: 20 }).map((_, i) => ({
      id: i,
      left: Math.random() * 100,
      top: Math.random() * 100,
      size: Math.random() * 3 + 2, // 2px to 5px
      delay: Math.random() * -30,
      duration: Math.random() * 20 + 15,
    }))
    setParticles(items)
  }, [])

  const stopSpinning = useCallback(
    (result: { number: number; isWinner: boolean }) => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
      setIsSpinning(false)
      isSpinningRef.current = false

      const finalNumber = result.number
      setCurrentNumber(finalNumber)
      setHistory((prev) => [finalNumber, ...prev].slice(0, 10))

      if (result.isWinner) {
        setWinningNumber(finalNumber)
        isRevealDelayingRef.current = true
        // Delay showing the winner congrats screen and confetti by 500ms
        // so the user can see the dial settle on their winning number first!
        setTimeout(() => {
          setHasWon(true)
          isRevealDelayingRef.current = false
          canvasConfetti({
            particleCount: 200,
            spread: 90,
            origin: { y: 0.6 },
            colors: ['#d8959b', '#f2d1d4', '#ffffff', '#FFD700'],
          })
        }, 500)
      }
    },
    [],
  )

  const startSpinning = useCallback(async () => {
    if (!gameState || gameState.status !== 'active' || isSpinning || hasWon)
      return

    setIsSpinning(true)
    isSpinningRef.current = true
    isRevealDelayingRef.current = false

    // Start visual spinning
    timerRef.current = setInterval(() => {
      setCurrentNumber(
        getRandomNumber(gameState.settings.min, gameState.settings.max),
      )
    }, 50)

    try {
      // Get secure number from server
      const result = await drawRandomNumber(eventId)

      // Stop after duration from settings
      setTimeout(() => {
        stopSpinning((result as any)?.data ?? result)
      }, gameState.settings.duration)
    } catch (error) {
      console.error('Failed to draw number:', error)
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
      setIsSpinning(false)
      isSpinningRef.current = false
      isRevealDelayingRef.current = false
    }
  }, [gameState, isSpinning, hasWon, stopSpinning])

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [])

  if (!gameState)
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-[#fbf8f8]">
        <div className="text-rose-dark animate-pulse text-2xl font-bold tracking-widest uppercase">
          กำลังเชื่อมต่อ...
        </div>
      </div>
    )

  const isGameActive = gameState.status === 'active'

  return (
    <LazyMotion features={domAnimation}>
      <div className="to-blush-soft relative flex h-full min-h-screen flex-col items-center justify-center overflow-hidden bg-linear-to-br from-[#fbf8f8] via-[#f9e9ea] p-4 pb-24 text-[#5c2d31] select-none sm:p-8 sm:pb-8">
        {/* Style block for floating shimmers */}
        <style>{`
          @keyframes float-particle {
            0% {
              transform: translateY(105vh) scale(0) rotate(0deg);
              opacity: 0;
            }
            10% {
              opacity: 0.6;
            }
            90% {
              opacity: 0.6;
            }
            100% {
              transform: translateY(-10vh) scale(1) rotate(360deg);
              opacity: 0;
            }
          }
          .rose-shimmer {
            position: absolute;
            bottom: -20px;
            background: radial-gradient(circle, rgba(216,149,155,0.7) 0%, rgba(242,209,212,0.8) 50%, rgba(216,149,155,0) 70%);
            border-radius: 50%;
            pointer-events: none;
            animation: float-particle var(--duration) linear infinite;
            animation-delay: var(--delay);
            z-index: 1;
          }
        `}</style>

        {/* Stable shimmers background */}
        <div className="pointer-events-none absolute inset-0 z-1 overflow-hidden">
          {particles.map((p) => (
            <div
              key={p.id}
              className="rose-shimmer"
              style={{
                left: `${p.left}%`,
                width: `${p.size}px`,
                height: `${p.size}px`,
                // @ts-expect-error unknown key
                '--delay': `${p.delay}s`,
                '--duration': `${p.duration}s`,
              }}
            />
          ))}
        </div>

        <m.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="z-10 w-full max-w-md space-y-6"
        >
          {/* Couples branding top header */}
          <div className="space-y-1 text-center">
            <span className="text-rose-dark/80 text-xs font-bold tracking-[0.35em] uppercase">
              OA & KOI WEDDING
            </span>
            <h1 className="text-3xl font-bold tracking-wider text-[#5c2d31] uppercase drop-shadow-sm">
              LUCKY DRAW
            </h1>
            <p className="text-rose-dark text-sm font-semibold">
              {isGameActive || isSpinning
                ? 'สุ่มตัวเลขให้ตรงกับเป้าหมายเพื่อลุ้นรางวัล!'
                : 'รอสักครู่ ขณะนี้ไม่มีกิจกรรม'}
            </p>
          </div>

          <Card className="border-rose-dark/25 rounded-[2.5rem] border bg-white/80 backdrop-blur-md">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg font-bold text-[#5c2d31]">
                  <MdiNumeric className="text-rose-dark text-2xl" />
                  สุ่มเลขนำโชค
                </CardTitle>
                <Sheet>
                  <SheetTrigger
                    render={
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-rose-dark hover:bg-rose-dark/10 size-10 rounded-full"
                      />
                    }
                  >
                    <MdiHistory className="text-2xl" />
                  </SheetTrigger>
                  <SheetContent
                    side="bottom"
                    className="border-rose-dark/10 rounded-t-[2.5rem] border bg-white px-6 pb-12 shadow-2xl"
                  >
                    <SheetHeader className="flex flex-row items-center justify-between pb-4">
                      <SheetTitle className="flex items-center gap-2 text-lg font-bold text-[#5c2d31]">
                        <MdiHistory className="text-xl" /> ประวัติการสุ่ม
                      </SheetTitle>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setHistory([])}
                        className="text-rose-dark/60 hover:bg-rose-dark/5 hover:text-rose-dark"
                      >
                        <MdiRefresh className="text-lg" />
                      </Button>
                    </SheetHeader>
                    <div className="flex flex-wrap gap-2 pt-4">
                      {history.length === 0 ? (
                        <p className="text-rose-dark/50 w-full py-8 text-center text-sm font-semibold">
                          ยังไม่มีประวัติการสุ่ม
                        </p>
                      ) : (
                        history.map((num, i) => (
                          <m.div
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            key={`${num}-${i}`}
                            className={`rounded-xl border px-4 py-2 font-bold shadow-sm ${
                              num === winningNumber
                                ? 'border-amber-400/40 bg-amber-500/10 text-amber-600'
                                : 'border-rose-dark/20 bg-rose-50 text-rose-950'
                            }`}
                          >
                            {num}
                          </m.div>
                        ))
                      )}
                    </div>
                  </SheetContent>
                </Sheet>
              </div>
            </CardHeader>
            <CardContent className="flex flex-col items-center gap-8 py-10">
              {/* Concentric spin dial */}
              <div className="border-rose-dark/35 relative flex size-56 items-center justify-center rounded-full border-[3px] bg-white/95 shadow-[0_15px_40px_rgba(216,149,155,0.15)]">
                {/* Inner dashed ring */}
                <div className="border-rose-dark/20 absolute inset-2.5 rounded-full border border-dashed" />

                <AnimatePresence mode="wait">
                  <m.span
                    key={isSpinning ? 'spinning' : (currentNumber ?? 'idle')}
                    initial={
                      isSpinning
                        ? { scale: 1, opacity: 1 }
                        : { scale: 0.8, opacity: 0 }
                    }
                    animate={{ scale: 1, opacity: 1 }}
                    exit={
                      isSpinning
                        ? { scale: 1, opacity: 1 }
                        : { scale: 1.2, opacity: 0 }
                    }
                    transition={{ duration: 0.15 }}
                    className={`bg-clip-text text-7xl font-bold tabular-nums drop-shadow-sm select-none ${
                      currentNumber === winningNumber && !isSpinning
                        ? 'bg-linear-to-b from-amber-400 via-yellow-500 to-amber-600 text-transparent'
                        : 'via-rose-dark bg-linear-to-b from-[#5c2d31] to-[#a35e64] text-transparent'
                    }`}
                  >
                    {currentNumber ?? '?'}
                  </m.span>
                </AnimatePresence>

                {/* Pulsing ring during spin */}
                {isSpinning && (
                  <div className="border-rose-dark/15 absolute inset-0 animate-ping rounded-full border opacity-30" />
                )}
              </div>

              {hasWon ? (
                <div className="space-y-1.5 text-center">
                  <div className="flex items-center justify-center gap-1.5 text-xl font-bold text-green-600">
                    <Sparkles className="size-5 animate-pulse text-green-500" />
                    ยินดีด้วย! คุณชนะแล้ว
                  </div>
                  <p className="text-rose-dark text-sm font-bold">
                    คุณได้รับหมายเลข {winningNumber}
                  </p>
                </div>
              ) : (
                /* Concentric styled START button */
                <div className="from-blush-soft via-rose-dark rounded-full bg-linear-to-b to-[#c78288] p-0.5 shadow-[0_10px_30px_rgba(216,149,155,0.3)] transition-all duration-300 active:scale-95">
                  <Button
                    size="lg"
                    onClick={startSpinning}
                    disabled={isSpinning || !isGameActive}
                    className="border-rose-dark/10 bg-rose-dark flex min-w-50 items-center justify-center gap-2 rounded-full border px-14 py-6.5 text-2xl font-bold tracking-widest text-white select-none hover:bg-[#c78288] disabled:opacity-50"
                  >
                    <MdiPlay className="text-3xl" />
                    {isSpinning ? 'สุ่ม...' : 'START'}
                  </Button>
                </div>
              )}

              {!isGameActive && !hasWon && !isSpinning && (
                <p className="animate-pulse text-sm font-bold text-red-500">
                  รอผู้ดูแลระบบเริ่มกิจกรรม
                </p>
              )}
            </CardContent>
          </Card>
        </m.div>
      </div>
    </LazyMotion>
  )
}
